import { ref } from 'vue'
import getPostsessionsData from '../../../../composables/getPostsessions'
import datesGenerator from './DatesGenerator'

const {
  TimeRanges,
  getThisWeekStartDate,
  getThisMonthStartDate,
  getHalfYearStartDate,
  getYearStartDate,
  getPastWeekDaysArray,
  getPastMonthDaysArray,
  convertTimeStampToDateString,
  getPast12Months,
  getMonthNameFromDate,
  getHalfYearWeeksArray,
  getWeekRangeForDate
} = datesGenerator()

const repsParams = 'Reptitions'
const kneeSleeveParams = ['knee', 'hamstring', 'shin', 'quad', 'calf']
const gloveParams = ['wristX', 'wristY', 'wristZ', 'pinky', 'ring', 'middle', 'index', 'thumb', 'grasp']

const processPostsessionsData = () => {
  const {
    offDeviceSessionsReps,
    fetchOffDeviceSessionsReps,
    fetchPostsessionsDataForDeviceType,
    postsessions
  } = getPostsessionsData()
  const glovePostsessionsData = ref([])
  const kneeSleevePostsessionsData = ref([])
  const pantsPostsessionsData = ref([])
  const groupedChartData = ref({})
  const nameToChartDataMap = {
    Glove: glovePostsessionsData,
    'Knee Sleeve': kneeSleevePostsessionsData,
    Pants: pantsPostsessionsData
  }
  const nameToParamsMap = {
    Glove: gloveParams,
    'Knee Sleeve': kneeSleeveParams,
    Pants: kneeSleeveParams
  }

  const processDailyData = (documents, minParam, maxParam, avgParam, device) => {
    const rightProcessedData = {}
    const leftProcessedData = {}
    const sessionReps = {}
    documents.forEach((document) => {
      const data = document.data
      const dateString = convertTimeStampToDateString(data.startTime)
      data.postsessions.forEach((postsession) => {
        if (postsession.repetitions) {
          if (sessionReps[dateString]) {
            sessionReps[dateString].repsMin = Math.min(postsession.repetitions, sessionReps[dateString].repsMin)
            sessionReps[dateString].repsMax = Math.max(postsession.repetitions, sessionReps[dateString].repsMax)
            sessionReps[dateString].repsAvg += postsession.repetitions
            sessionReps[dateString].count += 1
          } else {
            sessionReps[dateString] = {}
            sessionReps[dateString].repsMin = postsession.repetitions
            sessionReps[dateString].repsMax = postsession.repetitions
            sessionReps[dateString].repsAvg = postsession.repetitions
            sessionReps[dateString].count = 1
          }
        }
        if (postsession.side === 'Right') {
          if (rightProcessedData[dateString]) {
            nameToParamsMap[device].forEach((param) => {
              rightProcessedData[dateString][`${param}Min`] = Math.min(postsession[`${param}${minParam}`], rightProcessedData[dateString][[`${param}Min`]])
              rightProcessedData[dateString][`${param}Max`] = Math.max(postsession[`${param}${maxParam}`], rightProcessedData[dateString][[`${param}Max`]])
              rightProcessedData[dateString][`${param}Avg`] += postsession[`${param}${avgParam}`]
            })
            rightProcessedData[dateString].count += 1
          } else {
            rightProcessedData[dateString] = {}
            nameToParamsMap[device].forEach((param) => {
              rightProcessedData[dateString][`${param}Min`] = postsession[`${param}${minParam}`]
              rightProcessedData[dateString][`${param}Max`] = postsession[`${param}${maxParam}`]
              rightProcessedData[dateString][`${param}Avg`] = postsession[`${param}${avgParam}`]
            })
            rightProcessedData[dateString].count = 1
          }
        } else {
          if (leftProcessedData[dateString]) {
            nameToParamsMap[device].forEach((param) => {
              leftProcessedData[dateString][`${param}Min`] = Math.min(postsession[`${param}${minParam}`], leftProcessedData[dateString][[`${param}Min`]])
              leftProcessedData[dateString][`${param}Max`] = Math.max(postsession[`${param}${maxParam}`], leftProcessedData[dateString][[`${param}Max`]])
              leftProcessedData[dateString][`${param}Avg`] += postsession[`${param}Avg`]
            })
            leftProcessedData[dateString].count += 1
          } else {
            leftProcessedData[dateString] = {}
            nameToParamsMap[device].forEach((param) => {
              leftProcessedData[dateString][`${param}Min`] = postsession[`${param}${minParam}`]
              leftProcessedData[dateString][`${param}Max`] = postsession[`${param}${maxParam}`]
              leftProcessedData[dateString][`${param}Avg`] = postsession[`${param}${avgParam}`]
            })
            leftProcessedData[dateString].count = 1
          }
        }
      })
    })
    for (const dateString in leftProcessedData) {
      nameToParamsMap[device].forEach((param) => {
        leftProcessedData[dateString][`${param}Avg`] /= leftProcessedData[dateString].count
      })
    }
    for (const dateString in rightProcessedData) {
      nameToParamsMap[device].forEach((param) => {
        rightProcessedData[dateString][`${param}Avg`] /= rightProcessedData[dateString].count
      })
    }
    for (const dateString in sessionReps) {
      sessionReps[dateString].repsAvg /= sessionReps[dateString].count
    }

    return { leftProcessedData, rightProcessedData, sessionReps }
  }

  const processDailyDataForReps = () => {
    const leftProcessedData = {}
    offDeviceSessionsReps.value.forEach((reps) => {
      const dateString = convertTimeStampToDateString(reps.startTime)
      if (leftProcessedData[dateString]) {
        leftProcessedData[dateString].repsMin = Math.min(reps.reps, leftProcessedData[dateString].repsMin)
        leftProcessedData[dateString].repsMax = Math.max(reps.reps, leftProcessedData[dateString].repsMax)
        leftProcessedData[dateString].repsAvg += reps.reps
        leftProcessedData[dateString].count += 1
      } else {
        leftProcessedData[dateString] = {}
        leftProcessedData[dateString].repsMin = reps.reps
        leftProcessedData[dateString].repsMax = reps.reps
        leftProcessedData[dateString].repsAvg = reps.reps
        leftProcessedData[dateString].count = 1
      }
    })

    for (const dateString in leftProcessedData) {
      leftProcessedData[dateString].repsAvg /= leftProcessedData[dateString].count
    }
    return leftProcessedData
  }

  const processHalfYearData = (documents, minParam, maxParam, avgParam, device) => {
    const leftProcessedData = {}
    const rightProcessedData = {}

    const sessionReps = {}

    documents.forEach((document) => {
      const data = document.data
      const dateString = convertTimeStampToDateString(data.startTime)
      const week = getWeekRangeForDate(dateString)
      data.postsessions.forEach((postsession) => {
        if (postsession.repetitions) {
          if (sessionReps[week]) {
            sessionReps[week].repsMin = Math.min(postsession.repetitions, sessionReps[week].repsMin)
            sessionReps[week].repsMax = Math.max(postsession.repetitions, sessionReps[week].repsMax)
            sessionReps[week].repsAvg += postsession.repetitions
            sessionReps[week].count += 1
          } else {
            sessionReps[week] = {}
            sessionReps[week].repsMin = postsession.repetitions
            sessionReps[week].repsMax = postsession.repetitions
            sessionReps[week].repsAvg = postsession.repetitions
            sessionReps[week].count = 1
          }
        }

        if (postsession.side === 'Right') {
          if (rightProcessedData[week]) {
            nameToParamsMap[device].forEach((param) => {
              rightProcessedData[week][`${param}Min`] = Math.min(postsession[`${param}${minParam}`], rightProcessedData[week][[`${param}Min`]])
              rightProcessedData[week][`${param}Max`] = Math.max(postsession[`${param}${maxParam}`], rightProcessedData[week][[`${param}Max`]])
              rightProcessedData[week][`${param}Avg`] += postsession[`${param}${avgParam}`]
            })
            rightProcessedData[week].count += 1
          } else {
            rightProcessedData[week] = {}
            nameToParamsMap[device].forEach((param) => {
              rightProcessedData[week][`${param}Min`] = postsession[`${param}${minParam}`]
              rightProcessedData[week][`${param}Max`] = postsession[`${param}${maxParam}`]
              rightProcessedData[week][`${param}Avg`] = postsession[`${param}${avgParam}`]
            })
            rightProcessedData[week].count = 1
          }
        } else {
          if (leftProcessedData[week]) {
            nameToParamsMap[device].forEach((param) => {
              leftProcessedData[week][`${param}Min`] = Math.min(postsession[`${param}${minParam}`], leftProcessedData[week][[`${param}Min`]])
              leftProcessedData[week][`${param}Max`] = Math.max(postsession[`${param}${maxParam}`], leftProcessedData[week][[`${param}Max`]])
              leftProcessedData[week][`${param}Avg`] += postsession[`${param}Avg`]
            })
            leftProcessedData[week].count += 1
          } else {
            leftProcessedData[week] = {}
            nameToParamsMap[device].forEach((param) => {
              leftProcessedData[week][`${param}Min`] = postsession[`${param}${minParam}`]
              leftProcessedData[week][`${param}Max`] = postsession[`${param}${maxParam}`]
              leftProcessedData[week][`${param}Avg`] = postsession[`${param}${avgParam}`]
            })
            leftProcessedData[week].count = 1
          }
        }
      })
    })
    for (const week in leftProcessedData) {
      nameToParamsMap[device].forEach((param) => {
        leftProcessedData[week][`${param}Avg`] /= leftProcessedData[week].count
      })
    }
    for (const week in rightProcessedData) {
      nameToParamsMap[device].forEach((param) => {
        rightProcessedData[week][`${param}Avg`] /= rightProcessedData[week].count
      })
    }

    for (const week in sessionReps) {
      sessionReps[week].repsAvg /= sessionReps[week].count
    }
    return { leftProcessedData, rightProcessedData, sessionReps }
  }

  const processHalfYearDataForReps = () => {
    const leftProcessedData = {}
    offDeviceSessionsReps.value.forEach((reps) => {
      const dateString = convertTimeStampToDateString(reps.startTime)
      const week = getWeekRangeForDate(dateString)

      if (leftProcessedData[week]) {
        leftProcessedData[week].repsMin = Math.min(reps.reps, leftProcessedData[week].repsMin)
        leftProcessedData[week].repsMax = Math.max(reps.reps, leftProcessedData[week].repsMax)
        leftProcessedData[week].repsAvg += reps.reps
        leftProcessedData[week].count += 1
      } else {
        leftProcessedData[week] = {}
        leftProcessedData[week].repsMin = reps.reps
        leftProcessedData[week].repsMax = reps.reps
        leftProcessedData[week].repsAvg = reps.reps
        leftProcessedData[week].count = 1
      }
    })

    for (const week in leftProcessedData) {
      leftProcessedData[week].repsAvg /= leftProcessedData[week].count
    }
    return leftProcessedData
  }

  const processYearlyData = (documents, minParam, maxParam, avgParam, device) => {
    const leftProcessedData = {}
    const rightProcessedData = {}
    const sessionReps = {}

    documents.forEach((document) => {
      const data = document.data
      const dateString = convertTimeStampToDateString(data.startTime)
      const month = getMonthNameFromDate(dateString)
      data.postsessions.forEach((postsession) => {
        if (postsession.repetitions) {
          if (sessionReps[month]) {
            sessionReps[month].repsMin = Math.min(postsession.repetitions, sessionReps[month].repsMin)
            sessionReps[month].repsMax = Math.max(postsession.repetitions, sessionReps[month].repsMax)
            sessionReps[month].repsAvg += postsession.repetitions
            sessionReps[month].count += 1
          } else {
            sessionReps[month] = {}
            sessionReps[month].repsMin = postsession.repetitions
            sessionReps[month].repsMax = postsession.repetitions
            sessionReps[month].repsAvg = postsession.repetitions
            sessionReps[month].count = 1
          }
        }

        if (postsession.side === 'Right') {
          if (rightProcessedData[month]) {
            nameToParamsMap[device].forEach((param) => {
              rightProcessedData[month][`${param}Min`] = Math.min(postsession[`${param}${minParam}`], rightProcessedData[month][[`${param}Min`]])
              rightProcessedData[month][`${param}Max`] = Math.max(postsession[`${param}${maxParam}`], rightProcessedData[month][[`${param}Max`]])
              rightProcessedData[month][`${param}Avg`] += postsession[`${param}${avgParam}`]
            })
            rightProcessedData[month].count += 1
          } else {
            rightProcessedData[month] = {}
            nameToParamsMap[device].forEach((param) => {
              rightProcessedData[month][`${param}Min`] = postsession[`${param}${minParam}`]
              rightProcessedData[month][`${param}Max`] = postsession[`${param}${maxParam}`]
              rightProcessedData[month][`${param}Avg`] = postsession[`${param}${avgParam}`]
            })
            rightProcessedData[month].count = 1
          }
        } else {
          if (leftProcessedData[month]) {
            nameToParamsMap[device].forEach((param) => {
              leftProcessedData[month][`${param}Min`] = Math.min(postsession[`${param}${minParam}`], leftProcessedData[month][[`${param}Min`]])
              leftProcessedData[month][`${param}Max`] = Math.max(postsession[`${param}${maxParam}`], leftProcessedData[month][[`${param}Max`]])
              leftProcessedData[month][`${param}Avg`] += postsession[`${param}Avg`]
            })
            leftProcessedData[month].count += 1
          } else {
            leftProcessedData[month] = {}
            nameToParamsMap[device].forEach((param) => {
              leftProcessedData[month][`${param}Min`] = postsession[`${param}${minParam}`]
              leftProcessedData[month][`${param}Max`] = postsession[`${param}${maxParam}`]
              leftProcessedData[month][`${param}Avg`] = postsession[`${param}${avgParam}`]
            })
            leftProcessedData[month].count = 1
          }
        }
      })
    })
    for (const month in leftProcessedData) {
      nameToParamsMap[device].forEach((param) => {
        leftProcessedData[month][`${param}Avg`] /= leftProcessedData[month].count
      })
    }
    for (const month in rightProcessedData) {
      nameToParamsMap[device].forEach((param) => {
        rightProcessedData[month][`${param}Avg`] /= rightProcessedData[month].count
      })
    }
    for (const month in sessionReps) {
      sessionReps[month].repsAvg /= sessionReps[month].count
    }
    return { leftProcessedData, rightProcessedData, sessionReps }
  }

  const processYearlyDataForReps = () => {
    const leftProcessedData = {}
    offDeviceSessionsReps.value.forEach((reps) => {
      const dateString = convertTimeStampToDateString(reps.startTime)
      const month = getMonthNameFromDate(dateString)

      if (leftProcessedData[month]) {
        leftProcessedData[month].repsMin = Math.min(reps.reps, leftProcessedData[month].repsMin)
        leftProcessedData[month].repsMax = Math.max(reps.reps, leftProcessedData[month].repsMax)
        leftProcessedData[month].repsAvg += reps.reps
        leftProcessedData[month].count += 1
      } else {
        leftProcessedData[month] = {}
        leftProcessedData[month].repsMin = reps.reps
        leftProcessedData[month].repsMax = reps.reps
        leftProcessedData[month].repsAvg = reps.reps
        leftProcessedData[month].count = 1
      }
    })

    for (const month in leftProcessedData) {
      leftProcessedData[month].repsAvg /= leftProcessedData[month].count
    }
    return leftProcessedData
  }

  const processPostsessionsDataForDeviceType = async (deviceType, userId, timeRange, minParam = 'Min', maxParam = 'Max', avgParam = 'Avg') => {
    nameToChartDataMap[deviceType].value = []
    groupedChartData.value = {}

    if (timeRange === TimeRanges.lastWeek) {
      await fetchPostsessionsDataForDeviceType(userId, getThisWeekStartDate(), deviceType)
      await fetchOffDeviceSessionsReps(userId, getThisWeekStartDate(), deviceType)
      const { daysOfTheWeek, datesArray } = getPastWeekDaysArray()
      const { leftProcessedData, rightProcessedData, sessionReps } = processDailyData(postsessions.value, minParam, maxParam, avgParam, deviceType)
      const repsData = processDailyDataForReps()

      const chartData = []
      const avgs = []
      const sessionsRepsData = []
      const avgs2 = []

      for (const [index, date] of datesArray.entries()) {
        if (repsData[date]) {
          const dataPoint = {
            x: daysOfTheWeek[index],
            y: [repsData[date].repsMin, repsData[date].repsMax]
          }
          if (repsData[date].count === 1 && repsData[date].repsMin === repsData[date].repsMax) {
            // Add goals property to show a horizonal line on the chart
            dataPoint.goals = [{
              value: repsData[date].repsMin,
              strokeColor: '#F86624'
            }]
          }

          chartData.push(dataPoint)
          avgs.push(repsData[date].repsAvg)
        } else {
          chartData.push({
            x: daysOfTheWeek[index],
            y: [0, 0]
          })
          avgs.push(0)
        }

        if (sessionReps[date]) {
          const dataPoint = {
            x: daysOfTheWeek[index],
            y: [sessionReps[date].repsMin, sessionReps[date].repsMax]
          }

          // Check if this is a special case where count is 1 and min equals max
          if (sessionReps[date].count === 1 && sessionReps[date].repsMin === sessionReps[date].repsMax) {
            // Add goals property to show a horizonal line on the chart
            dataPoint.goals = [{
              value: sessionReps[date].repsMin,
              strokeColor: '#008FFB'
            }]
          }

          sessionsRepsData.push(dataPoint)
          avgs2.push(sessionReps[date].repsAvg)
        } else {
          sessionsRepsData.push({
            x: daysOfTheWeek[index],
            y: [0, 0]
          })
          avgs2.push(0)
        }
      }
      nameToChartDataMap[deviceType].value.push({
        id: repsParams.charAt(0).toUpperCase() + repsParams.slice(1),
        title: repsParams.charAt(0).toUpperCase() + repsParams.slice(1),
        series: [{ data: chartData, name: 'Off Device Reps' }, { data: sessionsRepsData, name: 'Session Reps' }],
        avgs: avgs,
        avgs2
      })

      nameToParamsMap[deviceType].forEach((param) => {
        const leftChartData = []
        const rightChartData = []
        const leftAvgs = []
        const rightAvgs = []
        for (const [index, date] of datesArray.entries()) {
          if (leftProcessedData[date]) {
            leftChartData.push({
              x: daysOfTheWeek[index],
              y: [leftProcessedData[date][`${param}Min`], leftProcessedData[date][`${param}Max`]]
            })
            leftAvgs.push(leftProcessedData[date][`${param}Avg`])
          } else {
            leftChartData.push({
              x: daysOfTheWeek[index],
              y: [0, 0]
            })
            leftAvgs.push(0)
          }
          if (rightProcessedData[date]) {
            rightChartData.push({
              x: daysOfTheWeek[index],
              y: [rightProcessedData[date][`${param}Min`], rightProcessedData[date][`${param}Max`]]
            })
            rightAvgs.push(rightProcessedData[date][`${param}Avg`])
          } else {
            rightChartData.push({
              x: daysOfTheWeek[index],
              y: [0, 0]
            })
            rightAvgs.push(0)
          }
        }
        nameToChartDataMap[deviceType].value.push({
          id: param.charAt(0).toUpperCase() + param.slice(1),
          title: `${param.charAt(0).toUpperCase() + param.slice(1)}`,
          series: [{ data: leftChartData, name: 'Left' }, { data: rightChartData, name: 'Right' }],
          avgs: leftAvgs,
          avgs2: rightAvgs
        })
      })
    } else if (timeRange === TimeRanges.lastMonth) {
      await fetchPostsessionsDataForDeviceType(userId, getThisMonthStartDate(), deviceType)
      await fetchOffDeviceSessionsReps(userId, getThisMonthStartDate(), deviceType)
      const daysOfMonth = getPastMonthDaysArray()
      const { leftProcessedData, rightProcessedData, sessionReps } = processDailyData(postsessions.value, minParam, maxParam, avgParam, deviceType)
      const repsData = processDailyDataForReps()

      const chartData = []
      const avgs = []
      const sessionsRepsData = []
      const avgs2 = []

      for (const date of daysOfMonth) {
        if (repsData[date]) {
          const dataPoint = {
            x: date,
            y: [repsData[date].repsMin, repsData[date].repsMax]
          }
          if (repsData[date].count === 1 && repsData[date].repsMin === repsData[date].repsMax) {
            // Add goals property to show a horizonal line on the chart
            dataPoint.goals = [{
              value: repsData[date].repsMin,
              strokeColor: '#F86624'
            }]
          }

          chartData.push(dataPoint)
          avgs.push(repsData[date].repsAvg)
        } else {
          chartData.push({
            x: date,
            y: [0, 0]
          })
          avgs.push(0)
        }

        if (sessionReps[date]) {
          const dataPoint = {
            x: date,
            y: [sessionReps[date].repsMin, sessionReps[date].repsMax]
          }

          // Check if this is a special case where count is 1 and min equals max
          if (sessionReps[date].count === 1 && sessionReps[date].repsMin === sessionReps[date].repsMax) {
            // Add goals property to show a horizonal line on the chart
            dataPoint.goals = [{
              value: sessionReps[date].repsMin,
              strokeColor: '#008FFB'
            }]
          }

          sessionsRepsData.push(dataPoint)
          avgs2.push(sessionReps[date].repsAvg)
        } else {
          sessionsRepsData.push({
            x: date,
            y: [0, 0]
          })
          avgs2.push(0)
        }
      }
      nameToChartDataMap[deviceType].value.push({
        id: repsParams.charAt(0).toUpperCase() + repsParams.slice(1),
        title: repsParams.charAt(0).toUpperCase() + repsParams.slice(1),
        series: [{ data: chartData, name: 'Off Device Reps' }, { data: sessionsRepsData, name: 'Session Reps' }],
        avgs: avgs,
        avgs2
      })

      nameToParamsMap[deviceType].forEach((param) => {
        const leftChartData = []
        const leftAvgs = []
        const rightChartData = []
        const rightAvgs = []

        for (const date of daysOfMonth) {
          if (leftProcessedData[date]) {
            leftChartData.push({
              x: date,
              y: [leftProcessedData[date][`${param}Min`], leftProcessedData[date][`${param}Max`]]
            })
            leftAvgs.push(leftProcessedData[date][`${param}Avg`])
          } else {
            leftChartData.push({
              x: date,
              y: [0, 0]
            })
            leftAvgs.push(0)
          }

          if (rightProcessedData[date]) {
            rightChartData.push({
              x: date,
              y: [rightProcessedData[date][`${param}Min`], rightProcessedData[date][`${param}Max`]]
            })
            rightAvgs.push(rightProcessedData[date][`${param}Avg`])
          } else {
            rightChartData.push({
              x: date,
              y: [0, 0]
            })
            rightAvgs.push(0)
          }
        }
        nameToChartDataMap[deviceType].value.push({
          id: param.charAt(0).toUpperCase() + param.slice(1),
          title: param.charAt(0).toUpperCase() + param.slice(1),
          series: [{ data: leftChartData, name: 'Left' }, { data: rightChartData, name: 'Right' }],
          avgs: leftAvgs,
          avgs2: rightAvgs
        })
      })
    } else if (timeRange === TimeRanges.last6Monts) {
      await fetchPostsessionsDataForDeviceType(userId, getHalfYearStartDate(), deviceType)
      await fetchOffDeviceSessionsReps(userId, getHalfYearStartDate(), deviceType)
      const weeksInHalfYear = getHalfYearWeeksArray()
      const { leftProcessedData, rightProcessedData, sessionReps } = processHalfYearData(postsessions.value, minParam, maxParam, avgParam, deviceType)
      const repsData = processHalfYearDataForReps()

      const chartData = []
      const avgs = []
      const sessionsRepsData = []
      const avgs2 = []

      for (const week of weeksInHalfYear) {
        if (repsData[week]) {
          const dataPoint = {
            x: week,
            y: [repsData[week].repsMin, repsData[week].repsMax]
          }
          if (repsData[week].count === 1 && repsData[week].repsMin === repsData[week].repsMax) {
            // Add goals property to show a horizonal line on the chart
            dataPoint.goals = [{
              value: repsData[week].repsMin,
              strokeColor: '#F86624'
            }]
          }

          chartData.push(dataPoint)
          avgs.push(repsData[week].repsAvg)
        } else {
          chartData.push({
            x: week,
            y: [0, 0]
          })
          avgs.push(0)
        }

        if (sessionReps[week]) {
          const dataPoint = {
            x: week,
            y: [sessionReps[week].repsMin, sessionReps[week].repsMax]
          }

          // Check if this is a special case where count is 1 and min equals max
          if (sessionReps[week].count === 1 && sessionReps[week].repsMin === sessionReps[week].repsMax) {
            // Add goals property to show a horizonal line on the chart
            dataPoint.goals = [{
              value: sessionReps[week].repsMin,
              strokeColor: '#008FFB'
            }]
          }

          sessionsRepsData.push(dataPoint)
          avgs2.push(sessionReps[week].repsAvg)
        } else {
          sessionsRepsData.push({
            x: week,
            y: [0, 0]
          })
          avgs2.push(0)
        }
      }
      nameToChartDataMap[deviceType].value.push({
        id: repsParams.charAt(0).toUpperCase() + repsParams.slice(1),
        title: repsParams.charAt(0).toUpperCase() + repsParams.slice(1),
        series: [{ data: chartData, name: 'Off Device Reps' }, { data: sessionsRepsData, name: 'Session Reps' }],
        avgs: avgs,
        avgs2
      })

      nameToParamsMap[deviceType].forEach((param) => {
        const leftChartData = []
        const leftAvgs = []
        const rightChartData = []
        const rightAvgs = []

        for (const week of weeksInHalfYear) {
          if (leftProcessedData[week]) {
            leftChartData.push({
              x: week,
              y: [leftProcessedData[week][`${param}Min`], leftProcessedData[week][`${param}Max`]]
            })
            leftAvgs.push(leftProcessedData[week][`${param}Avg`])
          } else {
            leftChartData.push({
              x: week,
              y: [0, 0]
            })
            leftAvgs.push(0)
          }

          if (rightProcessedData[week]) {
            rightChartData.push({
              x: week,
              y: [rightProcessedData[week][`${param}Min`], rightProcessedData[week][`${param}Max`]]
            })
            rightAvgs.push(rightProcessedData[week][`${param}Avg`])
          } else {
            rightChartData.push({
              x: week,
              y: [0, 0]
            })
            rightAvgs.push(0)
          }
        }
        nameToChartDataMap[deviceType].value.push({
          id: param.charAt(0).toUpperCase() + param.slice(1),
          title: `${param.charAt(0).toUpperCase() + param.slice(1)}`,
          series: [{ data: leftChartData, name: 'Left' }, { data: rightChartData, name: 'Right' }],
          avgs: leftAvgs,
          avgs2: rightAvgs
        })
      })
    } else {
      await fetchPostsessionsDataForDeviceType(userId, getYearStartDate(), deviceType)
      await fetchOffDeviceSessionsReps(userId, getYearStartDate(), deviceType)
      const monthsArray = getPast12Months()
      const { leftProcessedData, rightProcessedData, sessionReps } = processYearlyData(postsessions.value, minParam, maxParam, avgParam, deviceType)
      const repsData = processYearlyDataForReps()

      const chartData = []
      const avgs = []
      const sessionsRepsData = []
      const avgs2 = []

      for (const month of monthsArray) {
        if (repsData[month]) {
          const dataPoint = {
            x: month.slice(0, 3),
            y: [repsData[month].repsMin, repsData[month].repsMax]
          }
          if (repsData[month].count === 1 && repsData[month].repsMin === repsData[month].repsMax) {
            // Add goals property to show a horizonal line on the chart
            dataPoint.goals = [{
              value: repsData[month].repsMin,
              strokeColor: '#F86624'
            }]
          }
          chartData.push(dataPoint)
          avgs.push(repsData[month].repsAvg)
        } else {
          chartData.push({
            x: month.slice(0, 3),
            y: [0, 0]
          })
          avgs.push(0)
        }

        if (sessionReps[month]) {
          const dataPoint = {
            x: month.slice(0, 3),
            y: [sessionReps[month].repsMin, sessionReps[month].repsMax]
          }

          // Check if this is a special case where count is 1 and min equals max
          if (sessionReps[month].count === 1 && sessionReps[month].repsMin === sessionReps[month].repsMax) {
            // Add goals property to show a horizonal line on the chart
            dataPoint.goals = [{
              value: sessionReps[month].repsMin,
              strokeColor: '#008FFB'
            }]
          }

          sessionsRepsData.push(dataPoint)
          avgs2.push(sessionReps[month].repsAvg)
        } else {
          sessionsRepsData.push({
            x: month.slice(0, 3),
            y: [0, 0]
          })
          avgs2.push(0)
        }
      }
      nameToChartDataMap[deviceType].value.push({
        id: repsParams.charAt(0).toUpperCase() + repsParams.slice(1),
        title: repsParams.charAt(0).toUpperCase() + repsParams.slice(1),
        series: [{ data: chartData, name: 'Off Device Reps' }, { data: sessionsRepsData, name: 'Session Reps' }],
        avgs: avgs,
        avgs2
      })

      nameToParamsMap[deviceType].forEach((param) => {
        const leftChartData = []
        const leftAvgs = []
        const rightChartData = []
        const rightAvgs = []

        for (const month of monthsArray) {
          if (leftProcessedData[month]) {
            leftChartData.push({
              x: month.slice(0, 3),
              y: [leftProcessedData[month][`${param}Min`], leftProcessedData[month][`${param}Max`]]
            })
            leftAvgs.push(leftProcessedData[month][`${param}Avg`])
          } else {
            leftChartData.push({
              x: month.slice(0, 3),
              y: [0, 0]
            })
            leftAvgs.push(0)
          }

          if (rightProcessedData[month]) {
            rightChartData.push({
              x: month.slice(0, 3),
              y: [rightProcessedData[month][`${param}Min`], rightProcessedData[month][`${param}Max`]]
            })
            rightAvgs.push(rightProcessedData[month][`${param}Avg`])
          } else {
            rightChartData.push({
              x: month.slice(0, 3),
              y: [0, 0]
            })
            rightAvgs.push(0)
          }
        }
        nameToChartDataMap[deviceType].value.push({
          id: param.charAt(0).toUpperCase() + param.slice(1),
          title: param.charAt(0).toUpperCase() + param.slice(1),
          series: [{ data: leftChartData, name: 'Left' }, { data: rightChartData, name: 'Right' }],
          avgs: leftAvgs,
          avgs2: rightAvgs
        })
      })
    }

    const groups = {}
    for (const chart of nameToChartDataMap[deviceType].value) {
      const groupName = chart.title.split(' - ')[0] // Get the part before the dash (e.g., 'Knee', 'Hamstring')
      if (!groups[groupName]) {
        groups[groupName] = {
          charts: []
        }
      }
      groups[groupName].charts.push(chart)
    }
    groupedChartData.value = groups
  }

  return {
    groupedChartData,
    processPostsessionsDataForDeviceType
  }
}

export default processPostsessionsData
